import React, { useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import Input from 'shared/form-input';
import useForm from 'hooks/useForm';
import useGlobal from 'hooks/useGlobal';
import Select from 'shared/form-input/select';
import Button from 'button/loading';
import checkoutShippingAddressUpdateMutation from '@/queries/checkoutShippingAddressUpdate';
import updateShippingRateMutation from '@/queries/checkoutShippingRate';
import { getCheckoutWithShipping } from '@/queries/getCart';
import getCheckoutId from '@/queries/getCheckoutId';
import states from '../../states.json';

import s from './shipping-form.module.scss';

const requiredFields = ['lastName', 'address1', 'city', 'zip', 'province'];
const formFields = [...requiredFields, 'firstName', 'address2', 'country'];

export default function ShippingForm({
  isUser,
  submitEmailReady,
  setLocalErrors,
  submitGuestForm,
  emailLoading,
  emitGuestForm,
  cartData
}) {
  const { emit: globalEmit, state: globalState } = useGlobal();
  const { data } = useQuery(getCheckoutId);
  const editRef = useRef();

  const cartShippingResponse = useQuery(getCheckoutWithShipping, {
    variables: {
      id: data.checkout.checkoutId
    }
  });

  const nextStep = () => {
    return globalEmit.update({
      checkout: {
        step: 'payment'
      }
    });
  };

  const [updateShippingAddress, mutationData] = useMutation(
    checkoutShippingAddressUpdateMutation
  );
  const [updateShippingRate, shippingRateData] = useMutation(
    updateShippingRateMutation
  );

  const { state, events, emit } = useForm({ country: 'US' }, requiredFields);


  const hydrateForm = () => {
    const { email } = cartShippingResponse?.data?.node || {};
    const { shippingAddress } = cartData?.data?.node || cartShippingResponse?.data?.node || {};
    if (email && !isUser) {
      emitGuestForm.update({
        inputs: {
          email
        },
        touched: {
          email: true
        }
      });
    }
    if (!shippingAddress) return null;
    const inputValues = formFields.reduce(
      (acc, curr) => {
        return {
          inputs: {
            ...acc.inputs,
            [curr]: shippingAddress[curr === 'province' ? 'provinceCode' : curr]
          },
          touched: {
            ...acc.touched,
            [curr]: true
          }
        };
      },
      {
        inputs: {},
        touched: {}
      }
    );
    return emit.update(inputValues);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!submitEmailReady && !isUser) {
      return setLocalErrors(['Please enter an email or log in']);
    }
    const { shippingAddress, email } = cartShippingResponse?.data?.node || {};
    if (shippingAddress) {
      const inputsSame = Object.keys(state.inputs).reduce((acc, curr) => {
        return !acc
          ? acc
          : state.inputs[curr] ===
              shippingAddress[curr === 'province' ? 'provinceCode' : curr];
      }, true);
      if ((inputsSame && !email) || globalState.checkout.edit === 'contact') {
        try {
          await submitGuestForm(data.checkout.checkoutId);
        } catch (error) {
          return null;
        }
        return nextStep();
      }
      if (inputsSame) {
        return nextStep();
      }
    }
    try {
      const res = await updateShippingAddress({
        variables: {
          checkoutId: data.checkout.checkoutId,
          shippingAddress: state.inputs
        }
      });
      const { shippingLine, availableShippingRates } =
        res?.data.checkoutShippingAddressUpdateV2?.checkout || {};
      const shippingRates = availableShippingRates?.shippingRates;
      if (shippingRates?.length && !shippingLine) {
        try {
          const { handle } = shippingRates[0];
          await updateShippingRate({
            variables: {
              shippingRateHandle: handle,
              checkoutId: data.checkout.checkoutId
            }
          });
          if (isUser) {
            return nextStep();
          }
          if (!email) {
            try {
              await submitGuestForm(data.checkout.checkoutId);
            } catch (error) {
              return null;
            }
          }
          return nextStep();
        } catch (error) {
          console.log(error);
        }
      }
      if (isUser) {
        return nextStep();
      }
      if (!email) {
        try {
          await submitGuestForm(data.checkout.checkoutId);
        } catch (error) {
          return null;
        }
      }
      return nextStep();
    } catch (error) {
      console.log(error, 'SUBMIT ERROR');
      return null;
    }
  };

  const shouldLoad =
    (mutationData.loading && mutationData.called) ||
    (shippingRateData.loading && shippingRateData.called) ||
    emailLoading;

  useEffect(() => {
    console.log('hydrate');
    hydrateForm();
  }, [cartShippingResponse, cartData]);

  useEffect(() => {
    if (editRef?.current?.focus && globalState?.checkout?.edit === 'address') {
      editRef.current.focus();
    }
  }, [editRef, globalState?.checkout?.edit]);
  return (
    <div className={s.root}>
      <p className={s.title}>Shipping Address</p>
      <form onSubmit={handleSubmit}>
        <div className={s.flex}>
          <Input
            placeholder="First Name"
            name="firstName"
            className={s.input}
            state={state}
            ref={editRef}
            {...events}
          />
          <Input
            placeholder="Last Name"
            name="lastName"
            required
            className={s.input}
            state={state}
            {...events}
          />
        </div>
        <Input
          placeholder="Address"
          name="address1"
          className={s.input}
          required
          state={state}
          {...events}
        />
        <div className={s.flex}>
          <Input
            placeholder="Apartment, suite, etc. (optional)"
            name="address2"
            className={s.input}
            state={state}
            {...events}
          />
          <Input
            placeholder="City"
            required
            className={s.input}
            name="city"
            state={state}
            {...events}
          />
        </div>
        <div className={s.flex}>
          <Select
            value="US"
            name="country"
            placeholder="Country (US only)"
            className={s.input}
            state={state}
            {...events}
          >
            <option value="US">United States</option>
          </Select>
          <Select
            placeholder="Select a state"
            name="province"
            className={s.input}
            required
            state={state}
            {...events}
          >
            <option value="" hidden>
              Select a state
            </option>
            {states.map(({ name, value }) => (
              <option value={value} key={value}>
                {name}
              </option>
            ))}
          </Select>
          <Input
            name="zip"
            placeholder="ZIP code"
            required
            className={`${s.input} ${s.smaller}`}
            state={state}
            {...events}
          />
        </div>
        <div className={s.button}>
          <Button
            small
            type="submit"
            disabled={!state.submitReady || shouldLoad}
            loading={shouldLoad}
          >
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
}
